import React, { useState } from "react";
import AccordionSummary from "@mui/material/AccordionSummary";
import {
  Accordion,
  IconButton,
  Card,
  Typography,
  Tooltip,
  Box,
  Menu,
  MenuItem,
} from "@mui/material";
import HubOutlinedIcon from "@mui/icons-material/HubOutlined";
import Div from "@jumbo/shared/Div";
import { useTranslation } from "react-i18next";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import VisibilityIcon from "@mui/icons-material/Visibility";
import moment from "moment";
import AppRegistrationIcon from "@mui/icons-material/AppRegistration";
import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import useJumboAuth from "@jumbo/hooks/useJumboAuth";
import { SendAndArchiveOutlined } from "@mui/icons-material";
import MoneyOffIcon from "@mui/icons-material/MoneyOff";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import ScheduleIcon from "@mui/icons-material/Schedule";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import LocalAtmIcon from "@mui/icons-material/LocalAtm";
import PublicIcon from "@mui/icons-material/Public";
import PointOfSaleIcon from "@mui/icons-material/PointOfSale";
import PhoneInTalkIcon from "@mui/icons-material/PhoneInTalk";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined";
import NotificationsActiveOutlinedIcon from "@mui/icons-material/NotificationsActiveOutlined";
import OrderNotificationStatusDialog from "./OrderNotificationStatusDialog";
import {
  preparationStatusColor,
  preparationStatusLabelKey,
} from "./preparationStatusMeta";
import {
  isExternalOrder,
  getDeliveryTypeLabelKey,
  getSourceChannelLabelKey,
  getSourceChannelDisplay,
  getSourceChannelIconName,
} from "./externalOrderHelpers";
import StorefrontIcon from "@mui/icons-material/Storefront";
import LanguageIcon from "@mui/icons-material/Language";

import {
  getEkasaErrorText,
  mapEkasaStatus,
  mapOrderStatus,
  mapPaymentStatus,
  mapTerminalStatus,
} from "./orderStatusMapper";
import {
  DemoIcon,
  NotExecutedIcon,
  UserCancelIcon,
} from "./orderStatusIcons";

const USER_CANCELLED_PAYMENT_LABEL = "Platba bola zrušená zákazníkom";

const StatusIcon = ({ status }) =>
  status ? (
    <CheckCircleIcon sx={{ color: "#2C9E4B" }} />
  ) : (
    <HighlightOffIcon sx={{ color: "#DA1113" }} />
  );

const NotSufficientFundsIcon = () => <MoneyOffIcon sx={{ color: "#FFA500" }} />;
const InvalidTransactionIcon = () => (
  <ErrorOutlineIcon sx={{ color: "#DA1113" }} />
);

const PendingIcon = () => <ScheduleIcon sx={{ color: "#6B7280" }} />;
const PaymentCardIcon = () => <CreditCardIcon sx={{ color: "#6B7280" }} />;
const PaymentCashIcon = () => <LocalAtmIcon sx={{ color: "#6B7280" }} />;
const PaymentWebIcon = () => <PublicIcon sx={{ color: "#6B7280" }} />;
const PaymentAlreadyPaidIcon = () => <PointOfSaleIcon sx={{ color: "#6B7280" }} />;

const isDevDiagnosticsEnabled = () => {
  try {
    return (
      process.env.NODE_ENV !== "production" &&
      typeof window !== "undefined" &&
      window.location &&
      window.location.hostname === "localhost"
    );
  } catch (_e) {
    return false;
  }
};

const StatusStateIcon = ({ state, debugReason, title }) => {
  const icon =
    state === "ok" ? (
      <CheckCircleIcon sx={{ color: "#2C9E4B" }} />
    ) : state === "error" ? (
      <HighlightOffIcon sx={{ color: "#DA1113" }} />
    ) : state === "pending" ? (
      <PendingIcon />
    ) : state === "demo" ? (
      <DemoIcon />
    ) : state === "cancelled" ? (
      <UserCancelIcon />
    ) : state === "reversed" ? (
      <MoneyOffIcon sx={{ color: "#ED6C02" }} />
    ) : (
      <NotExecutedIcon />
    );

  if (title) {
    return (
      <Tooltip title={title}>
        <Box component="span" sx={{ display: "inline-flex" }}>
          {icon}
        </Box>
      </Tooltip>
    );
  }
  if (!isDevDiagnosticsEnabled() || !debugReason) return icon;
  return (
    <Tooltip title={debugReason}>
      <Box component="span" sx={{ display: "inline-flex" }}>
        {icon}
      </Box>
    </Tooltip>
  );
};

const OrderItem = ({
  item,
  currencyCode,
  onPreview,
  onDelete,
  onEkasaRegister,
  onEkasaPrintCopy,
  customer,
  onResendOrderToExternalSystem,
}) => {
  const { t } = useTranslation();
  // "Komunikácia" dropdown (before the kiosk name): send order to external system /
  // eKasa registration. Both actions are always shown and gated by a code modal.
  const [commMenuAnchor, setCommMenuAnchor] = useState(null);
  // Notification (SMS/push) delivery-status dialog.
  const [notifStatusOpen, setNotifStatusOpen] = useState(false);
  const { getAuthUser } = useJumboAuth();
  const { type } = getAuthUser() || {};
  const dsokOperator = type === 4;
  const { dsokSettings } = customer || {};
  const dsokType = customer?.dsokType;
  const devices = dsokSettings?.customer?.devices;
  let orderManagementStatus = dsokSettings?.orderManagement?.status;
  let enablePaymentStatus = true;
  let enablePrintOrEkasaStatus = true;
  let enableClientsInList = false;
  if (dsokType === "smartkiosk") {
    orderManagementStatus = dsokSettings?.integrations?.orderManagement?.status;
    enablePaymentStatus =
      dsokSettings?.customer?.customerUISettings?.enablePayment;
    enablePrintOrEkasaStatus =
      dsokSettings?.customer?.customerUISettings?.enablePrintOrEkasaStatus;
    enableClientsInList =
      dsokSettings?.customer?.customerUISettings?.enableClientsInList;
  }

  let deviceName = "";
  const demo = item?.demo;
  let posUserCancel = false;
  let notSufficientFunds = false;
  let invalidTransaction = false;
  for (const key in devices) {
    const device = devices[key];
    if (device?.uuid === item.kioskUUID) {
      deviceName = device?.deviceName;
    }
  }
  let posError = "";
  let transactionID = "";
  if (item?.orderStatus === "payment failed") {
    transactionID = item?.posError?.transactionID
      ? `TID: ${item?.posError?.transactionID}`
      : "";
    posError = item?.posError?.Result
      ? item?.posError?.Result
      : item?.posError?.error?.ErrorCondition;
    if (item?.posError?.Result && item?.posError?.error?.ErrorCondition) {
      posError = item?.posError?.error?.ErrorCondition;
    }
  }
  // Payment method is informational only. Must not imply "payment executed".
  const paymentMethodRaw =
    item?.payment?.method ??
    item?.paymentMethod ??
    item?.payment_method ??
    item?.paymentType ??
    item?.payment_type ??
    "";
  let paymentMethodKey = String(paymentMethodRaw || "")
    .trim()
    .toLowerCase();
  // ALBACON/Glory cash machine is a cash tender → show the "Hotovosť" (cash) icon.
  if (paymentMethodKey === "albaconcashmachine") {
    paymentMethodKey = "cash";
  }
  // Online payment (QR web / Besteron) → shown as a web payment: globe icon + "Web platba" label
  // + the "web" legend entry. QR online orders carry payment.method = "online".
  if (paymentMethodKey === "online") {
    paymentMethodKey = "web";
  }
  const paymentMethodType = paymentMethodKey
    ? t(`pages.dsokOrders.paymentMethods.${paymentMethodKey}`, {
        defaultValue: String(paymentMethodKey),
      })
    : "—";

  // External orders (e.g. AudioEcho phone orders): show source/channel, delivery type
  // and customer contact, none of which kiosk orders carry.
  const external = isExternalOrder(item);
  // Label z integrácie (systém: AudioEcho/WooCommerce), ikona z kanála (phone/web).
  const sourceSystem = item?.source?.system || item?.orderSource || "";
  const sourceChannel = item?.source?.channel || sourceSystem;
  const sourceChannelKey = getSourceChannelLabelKey(sourceChannel);
  const sourceChannelDisplay = getSourceChannelDisplay(sourceSystem, t);
  const sourceChannelIcon = getSourceChannelIconName(sourceChannel);
  const deliveryType = item?.deliveryType || "";
  const deliveryTypeKey = getDeliveryTypeLabelKey(deliveryType);
  let language = "";
  let reason = "";
  if (item?.storeCode) {
    const parts = item.storeCode.split("_");
    language = parts[parts.length - 1];
  }
  if (item?.posError?.reason && item?.posError?.reason === "EMV_USER_CANCEL") {
    posUserCancel = true;
    reason = "EMV_USER_CANCEL";
  }
  if (
    item?.posError?.reason &&
    item?.posError?.reason === "NOT_SUFFICIENT_FUNDS"
  ) {
    reason = "NOT_SUFFICIENT_FUNDS";
    notSufficientFunds = true;
  }
  if (
    item?.posError?.reason &&
    item?.posError?.reason === "INVALID_TRANSACTION"
  ) {
    reason = "INVALID_TRANSACTION";
    invalidTransaction = true;
  }
  let employeeName = "";
  let clientName = item?.additionalInfo?.authenticatedUser?.name || "";
  if (item?.additionalInfo?.authenticatedUser?.employee !== null) {
    employeeName =
      item?.additionalInfo?.authenticatedUser?.employee?.firstName +
      " " +
      item?.additionalInfo?.authenticatedUser?.employee?.lastName;
  }
  if (clientName === "") {
    clientName = item?.additionalInfo?.authenticatedUser?.name || "";
  }
  const createdAt = item?.createdAt
    ? moment(item?.createdAt).format("DD.MM.YYYY - HH:mm:ss")
    : "";
  const itemDate = createdAt.split(" - ")[0];
  const itemTime = createdAt.split(" - ")[1];

  /**
   * Orders list column mappings (backend -> UI)
   * - createdAt: item.createdAt
   * - order status: item.orderStatus (+ demo flag)
   * - order sent (external): item.orderSentStatus, item.internalOrderId, item.externalOrderId
   * - payment: based on mapping of payment/terminal fields (never inferred from paymentMethod)
   * - eKASA: based on item.ekasaStatus / item.eKasaResponse / item.eKasaError (never inferred from posStatus)
   * - payment method type: item.payment.method (informational)
   */
  const orderUi = mapOrderStatus(item);
  const paymentUi = mapPaymentStatus(item);
  const paymentReversedTitle =
    paymentUi.state === "reversed"
      ? `${t("pages.dsokOrders.storno.reversedLabel", "Stornované")}${
          item?.payment?.reversalAt
            ? " " + moment(item.payment.reversalAt).format("DD.MM.YYYY HH:mm")
            : ""
        }`
      : undefined;
  const terminalUi = mapTerminalStatus(item);
  const ekasaUi = mapEkasaStatus(item);
  const ekasaErrorText = getEkasaErrorText(item);
  const ekasaErrorTitle =
    ekasaUi.state === "error" && ekasaErrorText
      ? `${t("pages.dsokOrders.eKasaError")}: ${ekasaErrorText}`
      : undefined;
  // Fiscal storno (dobropis) indicator next to the eKASA status: orange=done, red=failed, grey=pending.
  const ekasaStornoState = item?.ekasaStorno?.state || null;
  const ekasaStornoUi =
    ekasaStornoState === "done"
      ? {
          color: "#ED6C02",
          title: `${t("pages.dsokOrders.storno.fiscalDone", "Dobropis vystavený")}${
            item?.ekasaStorno?.at
              ? " " + moment(item.ekasaStorno.at).format("DD.MM.YYYY HH:mm")
              : ""
          }`,
        }
      : ekasaStornoState === "failed"
        ? {
            color: "#D32F2F",
            title: `${t("pages.dsokOrders.storno.fiscalFailed", "Fiškálny dobropis zlyhal")}${
              item?.ekasaStorno?.error?.message
                ? `: ${item.ekasaStorno.error.message}`
                : ""
            }`,
          }
        : ekasaStornoState === "pending"
          ? {
              color: "#9E9E9E",
              title: t(
                "pages.dsokOrders.storno.fiscalPending",
                "Fiškálny dobropis prebieha…",
              ),
            }
          : null;
  // Opätovná registrácia dokladu do eKasy: len pre zákazníka cez DLA, keď POS prešiel,
  // doklad nie je zaregistrovaný a je uložená chyba eKasy. Backend (/dsok/order/ekasa-register)
  // podporuje iba Portos, preto pri inom type eKasy na zariadení tlačidlo nezobrazíme.
  // devices môže byť pole aj objekt (backend getDeviceByUUID podporuje oboje).
  const itemDevice =
    devices && typeof devices === "object"
      ? Object.values(devices).find((d) => d?.uuid && d.uuid === item?.kioskUUID)
      : null;
  const deviceEkasaType = itemDevice?.ekasa?.eKasaType;
  const canRegisterEkasa =
    dsokSettings?.customer?.communicationType === "DLA" &&
    item?.posStatus === true &&
    item?.ekasaStatus !== true &&
    Boolean(ekasaErrorText) &&
    (!deviceEkasaType || deviceEkasaType === "portos_sk");
  // Kópia dokladu (eKasa Portos cez DLA): len pre zaregistrované doklady. Portos kópiu nájde
  // iba podľa externalId, ktorý sa posiela od 28.09.2026 (ekasaExternalId) — staršie objednávky
  // majú tlačidlo neaktívne s vysvetlením.
  const showEkasaCopy =
    typeof onEkasaPrintCopy === "function" &&
    dsokSettings?.customer?.communicationType === "DLA" &&
    item?.ekasaStatus === true &&
    deviceEkasaType === "portos_sk";
  const canPrintEkasaCopy =
    showEkasaCopy && item?.ekasaExternalId === String(item?._id);
  const userCancelledPayment =
    paymentUi.state === "cancelled" || terminalUi.state === "cancelled";
  if (userCancelledPayment && reason === "") {
    reason = "CANCELLED_BY_USER";
  }

  // Desktop column layout: keep widths stable across all rows.
  // Note: visibility flags are customer-level, so they are consistent for all rows.
  const gridTemplateColumns = React.useMemo(() => {
    const cols = [];
    cols.push("180px"); // Kiosk
    cols.push("160px"); // Created
    if (enableClientsInList) cols.push("220px"); // Customer
    cols.push("180px"); // Order status
    cols.push("160px"); // Order sent / Obj.ID / Code
    if (enablePaymentStatus || enablePrintOrEkasaStatus) cols.push("90px"); // Payment / E-KASA (combined)
    cols.push("110px"); // Total / payment type
    cols.push("150px"); // Actions
    return cols.join(" ");
  }, [enableClientsInList, enablePaymentStatus, enablePrintOrEkasaStatus]);

  // Ikona sa zobrazí len ak má zákazník zapnutú službu notifikácií objednávok
  // (hlavný spínač). Dialóg rendruje ľubovoľný kanál z doručeniek, takže e-mail
  // a push pribudnú automaticky, keď budú aktívne.
  const orderNotificationsEnabled =
    dsokSettings?.settings?.orderNotifications?.isActive === true;
  const notifStatusButton =
    item?.externalOrderId && orderNotificationsEnabled ? (
      <Tooltip title={t("pages.dsokOrders.notifications.title", "Stav notifikácií")}>
        <IconButton
          size="small"
          onClick={() => setNotifStatusOpen(true)}
          sx={{ p: 0.25 }}
        >
          <NotificationsActiveOutlinedIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    ) : null;

  return (
    <Card sx={{ mb: 1 }}>
      <OrderNotificationStatusDialog
        open={notifStatusOpen}
        onClose={() => setNotifStatusOpen(false)}
        externalOrderId={item?.externalOrderId}
      />
      <Accordion expanded={false} square sx={{ borderRadius: 2 }}>
        <AccordionSummary
          aria-controls="panel1bh-content"
          id="panel1bh-header"
          sx={{
            px: 3,
            flexDirection: "row-reverse",
            "& .MuiAccordionSummary-content": {
              width: "100%",
              minWidth: 0,
              alignItems: { xs: "stretch", md: "start" },
              justifyContent: { xs: "flex-start", md: "start" },
              display: { xs: "flex", md: "grid" },
              flexDirection: { xs: "column", md: "unset" },
              rowGap: { xs: 1, md: 0 },
              gridTemplateColumns: { md: gridTemplateColumns },
              columnGap: { md: 2 },

              "&.Mui-expanded": {
                margin: "12px 0",
              },
            },
            ".MuiAccordionSummary-expandIconWrapper": {
              color: "text.secondary",
              transform: "none",
              height: 28,
              width: 28,
              alignItems: "center",
              justifyContent: "center",
              mr: 1,

              "&.Mui-expanded": {
                transform: "none",
                color: "primary.main",
                borderColor: "primary.main",
              },

              "& svg": {
                fontSize: "1.25rem",
              },
            },
          }}
        >
          <Div
            sx={{
              minWidth: 0,
              overflow: "hidden",
            }}
          >
            <Typography
              fontSize={"12px"}
              variant={"h6"}
              color={"text.secondary"}
              mb={0.25}
              sx={{
                display: { xs: "none", md: "block" },
              }}
            >
              {t("pages.dsokOrders.device", { defaultValue: "Zariadenie" })}
            </Typography>
            {external ? (
              <>
                <Box
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 0.5,
                    px: 1,
                    py: 0.25,
                    borderRadius: 1,
                    fontSize: "12px",
                    fontWeight: 600,
                    bgcolor: "#EEF2FF",
                    color: "#3730A3",
                  }}
                >
                  {sourceChannelIcon === "store" ? (
                    <StorefrontIcon sx={{ fontSize: 16 }} />
                  ) : sourceChannelIcon === "phone" ? (
                    <PhoneInTalkIcon sx={{ fontSize: 16 }} />
                  ) : (
                    <LanguageIcon sx={{ fontSize: 16 }} />
                  )}
                  {sourceChannelDisplay}
                </Box>
                {deliveryTypeKey ? (
                  <Box
                    sx={{
                      mt: 0.25,
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 0.5,
                      fontSize: "12px",
                      color: "text.secondary",
                    }}
                  >
                    {String(deliveryType).toLowerCase() === "delivery" ? (
                      <LocalShippingOutlinedIcon sx={{ fontSize: 16 }} />
                    ) : (
                      <StorefrontOutlinedIcon sx={{ fontSize: 16 }} />
                    )}
                    {t(deliveryTypeKey, deliveryType)}
                  </Box>
                ) : null}
                {language ? (
                  <Typography variant={"body1"}>[{language}]</Typography>
                ) : null}
              </>
            ) : (
              <>
                <Typography variant={"body1"}>{deviceName}</Typography>
                <Typography variant={"body1"}>[{language}]</Typography>
              </>
            )}
          </Div>
          <Div sx={{ minWidth: 0, overflow: "hidden" }}>
            <Typography
              fontSize={"12px"}
              variant={"h6"}
              color={"text.secondary"}
              mb={0.25}
              sx={{
                display: { xs: "none", md: "block" },
              }}
            >
              {t("pages.presentation.createdAt")}
            </Typography>
            <Typography variant={"body1"}>{itemDate}</Typography>
            <Typography variant={"body1"}>{itemTime}</Typography>
          </Div>

          {/* Mobile-only summary: show all key info in one compact stack */}
          <Div
            sx={{
              minWidth: 0,
              overflow: "hidden",
              display: { xs: "block", md: "none" },
            }}
          >
            <Typography
              variant="body2"
              sx={{ display: "flex", alignItems: "center", gap: 1 }}
            >
              <StatusStateIcon
                state={orderUi.state}
                debugReason={orderUi.debugReason}
              />
              <Box
                component="span"
                sx={{
                  minWidth: 0,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {item?.orderStatus || "—"} {demo ? " - DEMO" : ""}
              </Box>
            </Typography>
            {(posError || reason) && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ overflowWrap: "anywhere" }}
              >
                {posError} {reason ? <strong>{reason}</strong> : null}
              </Typography>
            )}
            {transactionID ? (
              <Typography variant="body2" color="text.secondary">
                {transactionID}
              </Typography>
            ) : null}

            <Box sx={{ mt: 0.5, overflowWrap: "anywhere" }}>
              <Typography variant="body2" color="text.secondary">
                {t("pages.dsokOrders.orderId")}/
                {t("pages.dsokOrders.orderCode")}:
              </Typography>
              <Typography variant="body2" sx={{ color: "text.primary" }}>
                {item?.internalOrderId || "—"}
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <Typography variant="body2" sx={{ color: "text.primary" }}>
                  {item?.externalOrderId || "—"}
                </Typography>
                {notifStatusButton}
              </Box>
            </Box>

            {(enablePaymentStatus || enablePrintOrEkasaStatus) && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.75,
                  flexWrap: "wrap",
                  mt: 0.5,
                }}
              >
                {enablePaymentStatus ? (
                  <Box
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 0.5,
                    }}
                  >
                    <StatusStateIcon
                      state={paymentUi.state === "ok" ? "ok" : paymentUi.state}
                      debugReason={`payment: ${paymentUi.debugReason || "—"}`}
                      title={
                        paymentUi.state === "reversed"
                          ? paymentReversedTitle
                          : paymentUi.state === "cancelled"
                            ? USER_CANCELLED_PAYMENT_LABEL
                            : undefined
                      }
                    />
                    <Typography variant="body2" color="text.secondary">
                      {t("pages.dsokOrders.payment")}
                    </Typography>
                  </Box>
                ) : null}
                {enablePaymentStatus && enablePrintOrEkasaStatus ? (
                  <Box component="span" sx={{ color: "text.secondary" }}>
                    /
                  </Box>
                ) : null}
                {enablePrintOrEkasaStatus ? (
                  <Box
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 0.5,
                    }}
                  >
                    <StatusStateIcon
                      state={ekasaUi.state}
                      debugReason={`ekasa: ${ekasaUi.debugReason || "—"}`}
                      title={ekasaErrorTitle}
                    />
                    <Typography variant="body2" color="text.secondary">
                      {t("pages.dsokDetails.ekasa")}
                    </Typography>
                    {ekasaStornoUi ? (
                      <Tooltip title={ekasaStornoUi.title}>
                        <Box component="span" sx={{ display: "inline-flex" }}>
                          <MoneyOffIcon
                            sx={{ color: ekasaStornoUi.color, fontSize: 18 }}
                          />
                        </Box>
                      </Tooltip>
                    ) : null}
                  </Box>
                ) : null}
              </Box>
            )}

            <Box
              sx={{ display: "flex", alignItems: "center", gap: 0.75, mt: 0.5 }}
            >
              <Typography variant="body1" sx={{ whiteSpace: "nowrap" }}>
                {`${
                  item?.grandTotal?.toFixed
                    ? item?.grandTotal.toFixed(2)
                    : item?.grandTotal
                } ${currencyCode || ""}`}
              </Typography>
              <Box component="span" sx={{ color: "text.secondary" }}>
                /
              </Box>
              {paymentMethodKey === "card" ? (
                <Tooltip title={paymentMethodType}>
                  <Box component="span" sx={{ display: "inline-flex" }}>
                    <PaymentCardIcon />
                  </Box>
                </Tooltip>
              ) : paymentMethodKey === "cash" ? (
                <Tooltip title={paymentMethodType}>
                  <Box component="span" sx={{ display: "inline-flex" }}>
                    <PaymentCashIcon />
                  </Box>
                </Tooltip>
              ) : paymentMethodKey === "web" ? (
                <Tooltip title={paymentMethodType}>
                  <Box component="span" sx={{ display: "inline-flex" }}>
                    <PaymentWebIcon />
                  </Box>
                </Tooltip>
              ) : paymentMethodKey === "alreadypaid" ? (
                <Tooltip title={paymentMethodType}>
                  <Box component="span" sx={{ display: "inline-flex" }}>
                    <PaymentAlreadyPaidIcon />
                  </Box>
                </Tooltip>
              ) : paymentMethodKey === "onpickup" ||
                paymentMethodKey === "instore" ? (
                <Tooltip title={paymentMethodType}>
                  <Box component="span" sx={{ display: "inline-flex" }}>
                    <PointOfSaleIcon sx={{ color: "#6B7280" }} />
                  </Box>
                </Tooltip>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  —
                </Typography>
              )}
            </Box>
          </Div>
          {enableClientsInList && (
            <>
              <Div sx={{ minWidth: 0, overflow: "hidden" }}>
                <Typography
                  fontSize={"12px"}
                  variant={"h6"}
                  color={"text.secondary"}
                  mb={0.25}
                  sx={{
                    display: { xs: "none", md: "block" },
                  }}
                >
                  {t("pages.dsokOrders.customer")}
                </Typography>
                <Typography variant={"body1"}>{clientName}</Typography>
                <Typography variant={"body1"}>{employeeName}</Typography>
              </Div>
            </>
          )}
          <Div
            sx={{
              minWidth: 0,
              overflow: "hidden",
              display: { xs: "none", md: "block" },
            }}
          >
            <Typography
              fontSize={"12px"}
              variant={"h6"}
              color={"text.secondary"}
              mb={0.25}
              sx={{
                display: { xs: "none", md: "block" },
              }}
            >
              {t("pages.dsokOrders.orderStatus")}
            </Typography>
            <Typography textTransform="capitalize" variant={"body1"}>
              <Box
                component="span"
                sx={{ display: "inline-flex", alignItems: "center", gap: 1 }}
              >
                <StatusStateIcon
                  state={orderUi.state}
                  debugReason={orderUi.debugReason}
                />
                <span>
                  {item?.orderStatus || "—"} {demo ? " - DEMO" : ""}
                </span>
              </Box>
              <br />
              {posError}
              {reason !== "" && <strong>{reason}</strong>}
            </Typography>
            <Typography variant={"body1"}>{transactionID}</Typography>
            {orderNotificationsEnabled && item?.preparationStatus ? (
              <Box
                component="span"
                sx={{
                  display: "inline-block",
                  mt: 0.5,
                  px: 1,
                  py: 0.25,
                  borderRadius: 1,
                  fontSize: "12px",
                  fontWeight: 600,
                  lineHeight: 1.6,
                  bgcolor: preparationStatusColor(item.preparationStatus).bg,
                  color: preparationStatusColor(item.preparationStatus).color,
                }}
              >
                {t(
                  preparationStatusLabelKey(item.preparationStatus),
                  item.preparationStatus,
                )}
              </Box>
            ) : null}
          </Div>
          <Div
            sx={{
              minWidth: 0,
              overflow: "hidden",
              overflowWrap: "anywhere",
              display: { xs: "none", md: "block" },
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 0.25,
                minWidth: 0,
                overflowWrap: "anywhere",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                {orderManagementStatus && (
                  <>
                    {userCancelledPayment && (
                      <UserCancelIcon status={item?.posStatus} />
                    )}
                    {notSufficientFunds && (
                      <NotSufficientFundsIcon status={item?.posStatus} />
                    )}
                    {invalidTransaction && (
                      <InvalidTransactionIcon status={item?.posStatus} />
                    )}
                    {!userCancelledPayment &&
                      !notSufficientFunds &&
                      !invalidTransaction && (
                        // External orders have no POS dispatch in this step — a fresh,
                        // accepted external order shows OK instead of a red "not sent".
                        <StatusIcon
                          status={external ? true : item?.orderSentStatus}
                        />
                      )}
                  </>
                )}
                <Box sx={{ display: "flex", flexDirection: "column" }}>
                  <Typography
                    fontSize={"12px"}
                    variant={"h6"}
                    color={"text.secondary"}
                    sx={{ display: { xs: "none", md: "block" } }}
                  >
                    {t("pages.dsokOrders.orderId")}/
                    {t("pages.dsokOrders.orderCode")}
                  </Typography>
                  <Typography variant={"body1"}>
                    {item?.internalOrderId || "—"}
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <Typography variant={"body1"}>
                      {item?.externalOrderId || "—"}
                    </Typography>
                    {notifStatusButton}
                  </Box>
                </Box>
              </Box>
            </Box>
          </Div>
          {(enablePaymentStatus || enablePrintOrEkasaStatus) && (
            <Div
              sx={{
                minWidth: 0,
                overflow: "hidden",
                display: { xs: "none", md: "block" },
              }}
            >
              <Typography
                fontSize={"12px"}
                variant={"h6"}
                color={"text.secondary"}
                mb={1}
                sx={{
                  display: { xs: "none", md: "block" },
                }}
              >
                {enablePaymentStatus ? t("pages.dsokOrders.payment") : null}
                {enablePaymentStatus && enablePrintOrEkasaStatus ? " / " : null}
                {enablePrintOrEkasaStatus ? t("pages.dsokDetails.ekasa") : null}
              </Typography>
              <Typography
                variant={"body1"}
                sx={{ display: "flex", alignItems: "center", gap: 0.75 }}
              >
                {enablePaymentStatus ? (
                  <StatusStateIcon
                    state={paymentUi.state === "ok" ? "ok" : paymentUi.state}
                    debugReason={`payment: ${paymentUi.debugReason || "—"}`}
                    title={
                      paymentUi.state === "cancelled"
                        ? USER_CANCELLED_PAYMENT_LABEL
                        : undefined
                    }
                  />
                ) : null}
                {enablePaymentStatus && enablePrintOrEkasaStatus ? (
                  <Box component="span" sx={{ color: "text.secondary" }}>
                    /
                  </Box>
                ) : null}
                {enablePrintOrEkasaStatus ? (
                  <StatusStateIcon
                    state={ekasaUi.state}
                    debugReason={`ekasa: ${ekasaUi.debugReason || "—"}`}
                    title={ekasaErrorTitle}
                  />
                ) : null}
              </Typography>
            </Div>
          )}
          <Div
            sx={{
              minWidth: 0,
              overflow: "hidden",
              display: { xs: "none", md: "block" },
            }}
          >
            <Typography
              fontSize={"12px"}
              variant={"h6"}
              color={"text.secondary"}
              mb={0.25}
              sx={{
                display: { xs: "none", md: "block" },
              }}
            >
              {t("pages.dsokOrders.total")} /{" "}
              {t("pages.dsokOrders.paymentMethodType")}
            </Typography>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.75,
                minWidth: 0,
              }}
            >
              <Typography variant={"body1"} sx={{ whiteSpace: "nowrap" }}>
                {`${
                  item?.grandTotal?.toFixed
                    ? item?.grandTotal.toFixed(2)
                    : item?.grandTotal
                } ${currencyCode || ""}`}
              </Typography>
              <Box
                component="span"
                sx={{ color: "text.secondary", flex: "0 0 auto" }}
              >
                /
              </Box>
              {paymentMethodKey === "card" ? (
                <Tooltip title={paymentMethodType}>
                  <Box
                    component="span"
                    sx={{ display: "inline-flex", flex: "0 0 auto" }}
                  >
                    <PaymentCardIcon />
                  </Box>
                </Tooltip>
              ) : paymentMethodKey === "cash" ? (
                <Tooltip title={paymentMethodType}>
                  <Box
                    component="span"
                    sx={{ display: "inline-flex", flex: "0 0 auto" }}
                  >
                    <PaymentCashIcon />
                  </Box>
                </Tooltip>
              ) : paymentMethodKey === "web" ? (
                <Tooltip title={paymentMethodType}>
                  <Box
                    component="span"
                    sx={{ display: "inline-flex", flex: "0 0 auto" }}
                  >
                    <PaymentWebIcon />
                  </Box>
                </Tooltip>
              ) : paymentMethodKey === "onpickup" ||
                paymentMethodKey === "instore" ? (
                <Tooltip title={paymentMethodType}>
                  <Box
                    component="span"
                    sx={{ display: "inline-flex", flex: "0 0 auto" }}
                  >
                    <PointOfSaleIcon sx={{ color: "#6B7280" }} />
                  </Box>
                </Tooltip>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  —
                </Typography>
              )}
            </Box>
          </Div>
          <Div
            sx={{
              minWidth: 0,
              overflow: "hidden",
            }}
          >
            <Typography
              fontSize={"12px"}
              variant={"h6"}
              color={"text.secondary"}
              mb={0.25}
              sx={{
                display: { xs: "none", md: "block" },
              }}
            >
              {t("pages.presentation.actions")}
            </Typography>
            <Div
              sx={{
                display: "flex",
                flex: { direction: "row", flexWrap: "wrap" },
                minWidth: 0,
              }}
            >
              <Div>
                <Tooltip
                  title={t("pages.dsokOrders.communication")}
                  placement="top"
                >
                  <IconButton
                    size="small"
                    variant={"contained"}
                    onClick={(e) => setCommMenuAnchor(e.currentTarget)}
                  >
                    <HubOutlinedIcon />
                  </IconButton>
                </Tooltip>
                <Menu
                  anchorEl={commMenuAnchor}
                  open={Boolean(commMenuAnchor)}
                  onClose={() => setCommMenuAnchor(null)}
                >
                  <MenuItem
                    onClick={() => {
                      setCommMenuAnchor(null);
                      onResendOrderToExternalSystem(item?._id);
                    }}
                  >
                    <SendAndArchiveOutlined
                      fontSize="small"
                      style={{ marginRight: 8 }}
                    />
                    {t("pages.dsokOrders.externalSystemRegistration")}
                  </MenuItem>
                  {/* Dočasne vypnuté — Registrácia fiškálneho dokladu (eKasa):
                  <MenuItem
                    onClick={() => {
                      setCommMenuAnchor(null);
                      onEkasaRegister(item?._id);
                    }}
                  >
                    <AppRegistrationIcon
                      fontSize="small"
                      style={{ marginRight: 8 }}
                    />
                    {t("pages.dsokOrders.eKasaRegistration")}
                  </MenuItem>
                  */}
                </Menu>
                {canRegisterEkasa && (
                  <Tooltip
                    title={`${t("pages.dsokOrders.eKasaRegistration")} — ${ekasaErrorText}`}
                    placement="top"
                  >
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => onEkasaRegister(item?._id)}
                      aria-label={t("pages.dsokOrders.eKasaRegistration")}
                    >
                      <AppRegistrationIcon />
                    </IconButton>
                  </Tooltip>
                )}
                {showEkasaCopy && (
                  <Tooltip
                    title={
                      canPrintEkasaCopy
                        ? t("pages.dsokOrders.ekasaCopy.print")
                        : t("pages.dsokOrders.ekasaCopy.unavailableNoExternalId")
                    }
                    placement="top"
                  >
                    <Box component="span">
                      <IconButton
                        size="small"
                        disabled={!canPrintEkasaCopy}
                        onClick={() => onEkasaPrintCopy(item?._id)}
                        aria-label={t("pages.dsokOrders.ekasaCopy.print")}
                      >
                        <PrintOutlinedIcon />
                      </IconButton>
                    </Box>
                  </Tooltip>
                )}
                <IconButton
                  size="small"
                  variant={"contained"}
                  onClick={onPreview}
                >
                  <Tooltip title={t("buttons.preview")} placement="top-start">
                    <VisibilityIcon />
                  </Tooltip>
                </IconButton>
                {/* "Poslať do systému" a "Registrácia dokladu v eKase" sú v "Komunikácia"
                    menu (prvé tlačidlo v stĺpci Akcie), obe cez modal s heslom. */}
                {!dsokOperator && (
                  <IconButton
                    size="small"
                    variant={"contained"}
                    onClick={() => onDelete(item?._id)}
                  >
                    <Tooltip title={t("buttons.delete")} placement="top">
                      <DeleteForeverIcon />
                    </Tooltip>
                  </IconButton>
                )}
              </Div>
            </Div>
          </Div>
        </AccordionSummary>
      </Accordion>
    </Card>
  );
};
/* Todo item prop define */
export default OrderItem;
